import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FixedTwoDecimalsPipe } from '../../../pipes/twodecimal.pipe';
import { ImageAnnotatorService } from '../../../services/image_anotate.service';

@Component({
  selector: 'app-detections-panel',
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    FixedTwoDecimalsPipe,
  ],
  templateUrl: './detections-panel.component.html',
  styleUrl: './detections-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetectionsPanelComponent {
  @Output() boxSelect = new EventEmitter<string | number>();
  @Output() boxToggle = new EventEmitter<{ id: string | number; visible: boolean }>();
  @Output() filterBoxesChange = new EventEmitter<boolean>();
  @Input() displayedKeys: string[] | null = null;
  @Input() displayedFields: Array<{ key: string; order?: number }> | null = null;
  private readonly _detections = signal<any[]>([]);
  readonly detections = computed(() => this._detections());

  readonly filterBoxes = signal(true);
  readonly filterTerm = signal('');
  is_all_visible = true;
  private readonly collapsedBoxIds = signal<Set<string | number>>(new Set());

  @Input() set inferences(value: any[] | null | undefined) {
    this.loadDetections(value ?? []);
  }

  readonly displayedDetections = computed(() => {
    const shouldFilterBoxes = this.filterBoxes();
    const allDetections = this.detections();
    if (!shouldFilterBoxes) return allDetections;
    return allDetections.filter((det: any) => det.task_rule_inferences === true);
  });

  readonly groupedDisplayedDetections = computed(() => {
    const detectionsToShow = this.displayedDetections();
    return this.groupDetectionsBy(detectionsToShow, 'name');
  });

  readonly finalFilteredDetections = computed(() => {
    const groupedDetections = this.groupedDisplayedDetections();
    const term = this.filterTerm().toLowerCase();
    if (!term) {
      return Object.entries(groupedDetections).map(([key, value]) => ({ key, value }));
    }
    return Object.entries(groupedDetections)
      .filter(([key]) => key.toLowerCase().includes(term))
      .map(([key, value]) => ({ key, value }));
  });

  constructor(private imageAnnotatorService: ImageAnnotatorService) {}

  onFilterBoxesChange(checked: boolean): void {
    this.filterBoxes.set(checked);
    this.filterBoxesChange.emit(checked);
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.filterTerm.set(filterValue);
  }

  changeAllVisibility(): void {
    const visible = !this.is_all_visible;
    this.is_all_visible = visible;
    this._detections.update(current => current.map((det) => ({ ...det, visible })));
    this._detections().forEach(det => {
      this.boxToggle.emit({ id: det.id, visible });
    });
  }

  toggleVisibility(key: string): void {
    let targetVisibility = true;
    this._detections.update(currentDetections => {
      const currentVisibility = currentDetections.find(d => d.name === key)?.visible;
      targetVisibility = !currentVisibility;
      return currentDetections.map(det =>
        det.name === key ? { ...det, visible: targetVisibility } : det
      );
    });

    const detections = this._detections();
    detections
      .filter(det => det.name === key)
      .forEach(det => this.boxToggle.emit({ id: det.id, visible: targetVisibility }));
  }

  trackByDetectionKey(_index: number, item: any): string {
    return item.key;
  }

  scrollToSection(sectionId: string | number): void {
    const element = document.getElementById(String(sectionId));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        element.focus();
        element.dispatchEvent(new Event('mouseenter'));
      }, 400);
    }
  }

  selectBox(sectionId: string | number): void {
    this.boxSelect.emit(sectionId);
    this.scrollToSection(sectionId);
  }

  toggleBoxVisibility(box: any): void {
    const nextVisible = !box.visible;
    box.visible = nextVisible;
    this.boxToggle.emit({ id: box.id, visible: nextVisible });
    if (nextVisible) {
      this.scrollToSection(box.id);
    }
  }

  toggleDetails(box: any): void {
    const detections = this._detections();
    const allIds = detections.map((det) => det.id);
    const isCollapsed = this.collapsedBoxIds().has(box.id);

    if (isCollapsed) {
      const next = new Set(allIds);
      next.delete(box.id);
      this.collapsedBoxIds.set(next);
      setTimeout(() => this.scrollToHeader(box.id), 0);
      return;
    }

    this.collapsedBoxIds.update((current) => {
      const next = new Set(current);
      next.add(box.id);
      return next;
    });
  }

  private scrollToHeader(boxId: string | number): void {
    const element = document.getElementById(`det-box-header-${boxId}`);
    if (element) {
      element.scrollIntoView({  block: 'center' });
    }
  }

  isCollapsed(box: any): boolean {
    return this.collapsedBoxIds().has(box.id);
  }

  getFormattedBoxValue(raw: any): { label?: string; value: string }[] {
    if (raw === null || raw === undefined) {
      return [];
    }

    const value = this.normalizeValue(raw);

    if (typeof value === 'object') {
      return this.extractJsonValues(value);
    }

    return [{ value: String(value) }];
  }

  getBoxEntries(box: any): { key: string; value: string }[] {
    if (!box || typeof box !== 'object') {
      return [];
    }

    const fieldList = this.displayedFields?.filter((field) => !!field?.key) ?? [];
    const normalized = this.displayedKeys?.filter(Boolean) ?? [];
    const keysFromFields = fieldList.map((field) => field.key);
    const activeKeys = keysFromFields.length ? keysFromFields : normalized;
    const entries = Object.entries(box)
      .filter(([key, value]) => {
        if (key === 'bounding_box') return false;
        if (activeKeys.length > 0 && !activeKeys.includes(key)) return false;
        if (value === null || value === undefined) return false;
        if (value === '-') return false;
        if (typeof value === 'string' && value.trim() === '') return false;
        return true;
      })
      .map(([key, value]) => {
        if (typeof value === 'object') {
          return { key, value: JSON.stringify(value) };
        }
        return { key, value: String(value) };
      });

    if (activeKeys.length === 0) {
      return entries;
    }

    if (fieldList.length) {
      const orderMap = new Map(fieldList.map((field, index) => [field.key, field.order ?? index]));
      return entries.sort((a, b) => (orderMap.get(a.key) ?? 0) - (orderMap.get(b.key) ?? 0));
    }

    const indexMap = new Map(normalized.map((key, index) => [key, index]));
    return entries.sort((a, b) => (indexMap.get(a.key) ?? 0) - (indexMap.get(b.key) ?? 0));
  }

  private loadDetections(inferences: any[]): void {
    const detections = (inferences ?? [])
      .map((det: any, index: number) => {
        const cleanedName = det?.name?.trim() || 'No se encontró';
        const isOther = cleanedName.split('_').slice(-1)[0].toLowerCase() === 'others';
        return {
          ...det,
          id: det?.id ?? index,
          name: cleanedName,
          visible: det?.visible ?? true,
          color: this.imageAnnotatorService.generateColorFromName(cleanedName, isOther),
          is_other: isOther,
        };
      });

    this._detections.set(detections);
    this.collapsedBoxIds.set(new Set(detections.map((det) => det.id)));
  }

  private groupDetectionsBy<T extends Record<string, any>>(array: T[], key: keyof T): Record<string | number, any> {
    const result = array.reduce((res, item) => {
      const groupKey = item[key] as string | number;
      if (!res[groupKey]) {
        res[groupKey] = {
          data: [],
          color: item['color'],
        };
      }
      res[groupKey].data.push(item);
      return res;
    }, {} as Record<string | number, any>);

    for (const groupKey in result) {
      result[groupKey].visible = result[groupKey].data.some((item: any) => item['visible']);
    }

    return result;
  }

  private normalizeValue(value: any): any {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const cleaned = value.replace(/^:\s*/, '').trim();

      if (cleaned.startsWith('{') && cleaned.endsWith('}')) {
        try {
          return JSON.parse(cleaned);
        } catch {
          return cleaned;
        }
      }

      return cleaned;
    }

    return value;
  }

  private extractJsonValues(obj: any): { label: string; value: string }[] {
    if (!obj || typeof obj !== 'object') {
      return [];
    }

    return Object.entries(obj)
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => ({
        label: key,
        value: String(value).trim()
      }));
  }
}
